/**
 * TransferDetailForm — Detail form for viewing/editing a transfer line item.
 * Shows the pending (staged) item or the selected (existing) item fields.
 * Extracted from CreateTransferView to keep the parent component smaller.
 */
import { Plus, Calendar } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { getDateCode } from '../../../utils/dateUtils';
import DatePickerDropdown from '../../../components/DatePickerDropdown';

/** A local weight entry (not yet persisted) */
export interface LocalWeight {
  tempId: number;
  weight: number;
}

/** A local line-item (not yet persisted, or loaded from server for edit) */
export interface LocalItem {
  tempId: number;
  serverId?: number;
  item_number: string;
  item_english_name?: string;
  item_chinese_name?: string;
  item_unit?: string;
  unit_weight?: number;
  quantity_request: number;
  quantity_shipped: number;
  weight_shipped: number;
  is_frozen: string;
  date_code: string;
  date_code_date: string; // YYYY-MM-DD for the date picker
  remark: string;
  weights: LocalWeight[];
  // Additional detail fields
  special_request?: string;
  avg_sold?: string;
  avg_back?: string;
  qs_type?: string;
  duration?: string;
  scan_complete?: string;
  qs_pend?: string;
  qs_rec_number?: string;
  salesperson_qs1?: string;
  q_item?: string;
}

interface TransferDetailFormProps {
  /** The item to display in the form (pending or selected), or null when nothing is selected */
  formItem: LocalItem | null;
  /** Callback to update a single field on the displayed item */
  updateField: (field: keyof LocalItem, value: any) => void;
  /** Whether the displayed item is a pending (staged) item not yet added to the table */
  isPending: boolean;
  /** Callback to commit the pending item into the items table */
  onCommitPendingItem: () => void;
  /** Number of items currently in the table, used for the empty state message */
  itemCount: number;
  /** Callback when quantity_shipped changes — parent handles auto-weight generation */
  onQuantityShippedChange: (qty: number) => void;
}

const TransferDetailForm = ({
  formItem,
  updateField,
  isPending,
  onCommitPendingItem,
  itemCount,
  onQuantityShippedChange,
}: TransferDetailFormProps) => {
  const { t } = useLanguage();

  return (
    <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
      {formItem ? (
        <div className="flex flex-col md:flex-row gap-4">
          {/* Left column: inputs */}
          <div className="w-full md:w-4/5 grid grid-cols-[100px_1fr] md:grid-cols-[140px_1fr] items-center gap-x-3 gap-y-2.5">
            {/* Row 1: Item No. (read-only) */}
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.itemNumber')}
            </span>
            <div className="text-sm font-mono font-medium text-gray-900 dark:text-gray-100">
              {formItem.item_number}
              {formItem.item_chinese_name && (
                <span className="ml-2 text-gray-500 dark:text-gray-400 font-sans">{formItem.item_chinese_name}</span>
              )}
              {formItem.item_english_name && (
                <span className="ml-2 text-gray-500 dark:text-gray-400 font-sans">{formItem.item_english_name}</span>
              )}
            </div>

            {/* Row 2: Qty Request + checkboxes (Frozen, Special Request) */}
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.quantityRequest')}
            </span>
            <div className="flex items-center gap-4 flex-wrap">
              <input
                type="number"
                value={formItem.quantity_request || ''}
                onChange={(e) => updateField('quantity_request', parseFloat(e.target.value) || 0)}
                className="w-24 px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-right"
              />
              <label className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={formItem.is_frozen === 'Y'}
                  onChange={(e) => updateField('is_frozen', e.target.checked ? 'Y' : 'N')}
                  className="rounded border-gray-300 dark:border-gray-600 text-primary-600"
                />
                {t('warehouseTransfer.isFrozen')}
              </label>
              <label className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={formItem.special_request === 'Y'}
                  onChange={(e) => updateField('special_request', e.target.checked ? 'Y' : 'N')}
                  className="rounded border-gray-300 dark:border-gray-600 text-primary-600"
                />
                {t('warehouseTransfer.specialRequest')}
              </label>
            </div>

            {/* Row 3: Qty Shipped */}
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.quantityShipped')}
            </span>
            <input
              type="number"
              value={formItem.quantity_shipped || ''}
              onChange={(e) => {
                const qty = parseFloat(e.target.value) || 0;
                onQuantityShippedChange(qty);
              }}
              className="w-24 px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-right"
            />

            {/* Row 4: Date Code + QS Type + Duration */}
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.dateCode')}
            </span>
            <div className="flex items-center gap-4 flex-wrap">
              <DatePickerDropdown
                selected={formItem.date_code_date || null}
                onSelect={(date) => {
                  updateField('date_code_date', date);
                  updateField('date_code', getDateCode(date));
                }}
              >
                {({ onClick }) => (
                  <button type="button" onClick={onClick} className="w-36 flex items-center justify-between px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    {formItem.date_code_date ? (() => { const d = new Date(formItem.date_code_date + 'T00:00:00'); return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }); })() : 'Select...'}
                    <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  </button>
                )}
              </DatePickerDropdown>
              {formItem.date_code && (
                <span className="text-sm font-mono font-medium text-indigo-600 dark:text-indigo-400">{formItem.date_code}</span>
              )}
              <span className="text-sm text-gray-500 dark:text-gray-400">{t('warehouseTransfer.qsType')}</span>
              <select
                value={formItem.qs_type || ''}
                onChange={(e) => updateField('qs_type', e.target.value)}
                className="w-28 px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">-- N/A --</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
              </select>
              <span className="text-sm text-gray-500 dark:text-gray-400">{t('warehouseTransfer.duration')}</span>
              <input
                type="number"
                value={formItem.duration || ''}
                onChange={(e) => updateField('duration', e.target.value)}
                className="w-20 px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-right"
              />
            </div>

            {/* Row 5: Salesperson (QS1) */}
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.salespersonQs1')}
            </span>
            <input
              type="text"
              placeholder="--- Salesperson for QS1 request ---"
              value={formItem.salesperson_qs1 || ''}
              onChange={(e) => updateField('salesperson_qs1', e.target.value)}
              className="w-full max-w-md px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />

            {/* Row 6: Remark */}
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
              {t('warehouseTransfer.remark')}
            </span>
            <input
              type="text"
              value={formItem.remark || ''}
              onChange={(e) => updateField('remark', e.target.value)}
              className="w-full max-w-md px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Right column: buttons */}
          <div className="w-full md:w-1/5 flex md:flex-col items-start justify-start gap-2">
            {isPending && (
              <button
                onClick={onCommitPendingItem}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t('warehouseTransfer.addItem')}
              </button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">
          {itemCount === 0
            ? t('warehouseTransfer.noItems')
            : t('warehouseTransfer.selectItemToEdit')}
        </p>
      )}
    </div>
  );
};

export default TransferDetailForm;
